import { useState, useEffect } from 'react';
import { feedbackService, FeedbackCreate } from '../../services/feedbackService';
import { uploadService } from '../../services/uploadService';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import type { Feedback } from '../../services/feedbackService';
import type { FeedbackNotificationMessage } from './feedbackTypes';

export function useFeedbackPage() {
  const [feedbackFormData, setFeedbackFormData] = useState<FeedbackCreate>({
    subject: '',
    message: '',
    image_url: undefined
  });
  const [feedbackImageFiles, setFeedbackImageFiles] = useState<File[]>([]);
  const [feedbackImagePreviews, setFeedbackImagePreviews] = useState<string[]>([]);
  const [feedbackImageNames, setFeedbackImageNames] = useState<string[]>([]);
  const [uploadingFeedbackImage, setUploadingFeedbackImage] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [editingFeedbackId, setEditingFeedbackId] = useState<string | null>(null);
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<FeedbackNotificationMessage | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadFeedback = async () => {
    try {
      setLoadingFeedback(true);
      const data = await feedbackService.getMyFeedback();
      setFeedbacks(data);
    } catch (error: any) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoadingFeedback(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  useEffect(() => {
    if (feedbackMessage) {
      const timer = setTimeout(() => setFeedbackMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedbackMessage]);

  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    } catch {
      const parts = url.split('/');
      const filename = parts[parts.length - 1] || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    }
  };

  const handleFeedbackImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validFiles: File[] = [];
    files.forEach((file) => {
      if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) return;
      validFiles.push(file);
    });
    if (files.length > 0 && validFiles.length < files.length) {
      setFeedbackMessage({ type: 'error', text: 'Some files are invalid. Please select only image files under 10MB each.' });
    }
    if (validFiles.length > 0) {
      const newPreviews: string[] = [];
      const newNames: string[] = [];
      let loadedCount = 0;
      validFiles.forEach((file) => {
        newNames.push(file.name);
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          loadedCount++;
          if (loadedCount === validFiles.length) {
            setFeedbackImagePreviews(prev => [...prev, ...newPreviews]);
            setFeedbackImageNames(prev => [...prev, ...newNames]);
          }
        };
        reader.readAsDataURL(file);
      });
      setFeedbackImageFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFeedbackImage = (index: number) => {
    const preview = feedbackImagePreviews[index];
    const newPreviews = feedbackImagePreviews.filter((_, i) => i !== index);
    const newNames = feedbackImageNames.filter((_, i) => i !== index);
    setFeedbackImagePreviews(newPreviews);
    setFeedbackImageNames(newNames);
    if (preview.startsWith('data:')) {
      let dataUrlCount = 0;
      for (let i = 0; i < index; i++) {
        if (feedbackImagePreviews[i].startsWith('data:')) dataUrlCount++;
      }
      setFeedbackImageFiles(prev => prev.filter((_, i) => i !== dataUrlCount));
    }
    if (newPreviews.length === 0) {
      setFeedbackFormData(prev => ({ ...prev, image_url: undefined }));
    }
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedbackLoading(true);
    setFeedbackMessage(null);
    try {
      let imageUrls: string[] = [];
      const existingUrls = feedbackImagePreviews
        .filter(preview => preview.startsWith('http://') || preview.startsWith('https://') || preview.startsWith('/api/'))
        .map(url => url.trim());
      imageUrls.push(...existingUrls);
      if (feedbackImageFiles.length > 0) {
        setUploadingFeedbackImage(true);
        const uploadedUrls = await Promise.all(
          feedbackImageFiles.map(file => uploadService.uploadImage(file))
        );
        imageUrls.push(...uploadedUrls);
        setUploadingFeedbackImage(false);
      }
      const imageUrl = imageUrls.length > 0 ? imageUrls.join(',') : undefined;
      const submitData = { ...feedbackFormData, image_url: imageUrl };
      if (editingFeedbackId) {
        await feedbackService.updateFeedback(editingFeedbackId, submitData);
        setFeedbackMessage({ type: 'success', text: 'Feedback updated successfully!' });
        track(AnalyticsEvents.FEEDBACK_UPDATED);
      } else {
        await feedbackService.createFeedback(submitData);
        setFeedbackMessage({ type: 'success', text: 'Feedback submitted successfully!' });
        track(AnalyticsEvents.FEEDBACK_SUBMITTED);
      }
      setFeedbackFormData({ subject: '', message: '', image_url: undefined });
      setFeedbackImageFiles([]);
      setFeedbackImagePreviews([]);
      setFeedbackImageNames([]);
      setShowFeedbackForm(false);
      setEditingFeedbackId(null);
      await loadFeedback();
    } catch (error: any) {
      setFeedbackMessage({ type: 'error', text: error.message || 'Failed to submit feedback' });
      setUploadingFeedbackImage(false);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleEditFeedback = (feedback: Feedback) => {
    setFeedbackFormData({
      subject: feedback.subject,
      message: feedback.message,
      image_url: feedback.image_url
    });
    const existingUrls = feedback.image_url
      ? feedback.image_url
          .split(',')
          .map(url => url.trim())
          .filter(url => url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/')))
      : [];
    setFeedbackImagePreviews(existingUrls);
    setFeedbackImageNames(existingUrls.map(url => getFilenameFromUrl(url)));
    setFeedbackImageFiles([]);
    setEditingFeedbackId(feedback.id);
    setShowFeedbackForm(true);
  };

  const handleDeleteFeedback = (feedback: Feedback) => {
    setFeedbackToDelete(feedback);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFeedback = async () => {
    if (!feedbackToDelete) return;
    try {
      await feedbackService.deleteFeedback(feedbackToDelete.id);
      setFeedbacks(prev => prev.filter(f => f.id !== feedbackToDelete.id));
      setFeedbackMessage({ type: 'success', text: 'Feedback deleted successfully!' });
      track(AnalyticsEvents.FEEDBACK_DELETED);
      if (editingFeedbackId === feedbackToDelete.id) {
        setShowFeedbackForm(false);
        setEditingFeedbackId(null);
      }
      setFeedbackToDelete(null);
      setShowDeleteConfirm(false);
    } catch (error: any) {
      setFeedbackMessage({ type: 'error', text: error.message || 'Failed to delete feedback' });
      setShowDeleteConfirm(false);
    }
  };

  const closeForm = () => {
    setShowFeedbackForm(false);
    setEditingFeedbackId(null);
    setFeedbackFormData({ subject: '', message: '', image_url: undefined });
    setFeedbackImageFiles([]);
    setFeedbackImagePreviews([]);
    setFeedbackImageNames([]);
  };

  const openAddForm = () => {
    setFeedbackFormData({ subject: '', message: '', image_url: undefined });
    setFeedbackImageFiles([]);
    setFeedbackImagePreviews([]);
    setFeedbackImageNames([]);
    setEditingFeedbackId(null);
    setShowFeedbackForm(true);
  };

  return {
    feedbackFormData,
    setFeedbackFormData,
    feedbackImageFiles,
    feedbackImagePreviews,
    feedbackImageNames,
    uploadingFeedbackImage,
    feedbackLoading,
    loadingFeedback,
    feedbacks,
    showFeedbackForm,
    setShowFeedbackForm,
    editingFeedbackId,
    openAddForm,
    feedbackMessage,
    setFeedbackMessage,
    showDeleteConfirm,
    setShowDeleteConfirm,
    loadFeedback,
    getFilenameFromUrl,
    handleFeedbackImageChange,
    removeFeedbackImage,
    handleFeedbackSubmit,
    handleEditFeedback,
    handleDeleteFeedback,
    confirmDeleteFeedback,
    closeForm,
  };
}
